import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { useAlert } from '@hooks/useAlert';
import { useNameMapQuery } from '@features/redux/apis/org';
import { setLastUpdated } from '@features/redux/slices/reportSlice';
import { ReportLayout } from '../ReportLayout';
import { OrgFilters } from '@components/OrgFilters';
import { Box, Grid, Link, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { KeyboardArrowDown, Search } from '@mui/icons-material';
import { EmployeeExport } from './EmployeeExport';
import { basicSort } from '@utils/sort';
import { none } from '@utils/summaries';
import { TotalCuts } from '../today/TotalCuts';
import { type EmployeeResponse, fetchOrgData, OrgUrls } from '@api/org';
import { AppRoutes } from '@consts/routes';
import { MaterialIdMap } from '@consts/materials';

const PAGE_SIZE = 20;

export type FilterInput = {
    companies: { [key: string]: boolean };
    resellers: { [key: string]: boolean };
    locations: { [key: string]: boolean };
};

const LABEL_MAP: Record<string, string> = {
    warrantyPercentage: 'Warranty %',
    totalCuts: '# Cuts',
    warranty: '# Warranty Cuts',
    successful: '# Successful Cuts',
};

export const EmployeeRank = ({ dateRange }: { dateRange: { startDate: string; endDate: string } }) => {
    const [employeesData, setEmployeesData] = useState<{ employees: EmployeeResponse[]; totalRecords: number }>({
        employees: [],
        totalRecords: 0,
    });
    const [loadingData, setLoadingData] = useState<boolean>(false);

    const dispatch = useDispatch();

    const [filters, setFilters] = useState<FilterInput>({
        companies: {},
        resellers: {},
        locations: {},
    });

    const { data: nameMap, isFetching: isLoading } = useNameMapQuery({});

    const [pageNum, setPageNum] = useState<number>(1);

    const [searchText, setSearchText] = useState<string>('');

    const [rankBy, setRankBy] = useState<'totalCuts' | 'warranty' | 'successful' | 'warrantyPercentage'>('totalCuts');
    const [material, setMaterial] = useState<keyof typeof MaterialIdMap | 'All'>('All');

    const setFilter = (key: keyof FilterInput, value: string, selectOne?: boolean) => {
        const isActive = filters[key][value];
        setFilters({ ...filters, [key]: { ...(selectOne ? {} : filters[key]), [value]: !isActive } });
        setPageNum(1);
    };

    const { IdToken } = useSelector((state: RootState) => state.auth);
    const { setAlert } = useAlert();

    const getEmployeesData = async () => {
        setLoadingData(true);
        try {
            const ranks = await fetchOrgData<{ employees: EmployeeResponse[]; totalRecords: number }>(
                IdToken!,
                OrgUrls.LocationEmployees,
                {
                    pageSize: PAGE_SIZE,
                    pageNum: pageNum - 1,
                    companyIds: Object.keys(filters.companies)
                        .filter((key) => filters.companies[key])
                        .join(','),
                    resellerIds: Object.keys(filters.resellers)
                        .filter((key) => filters.resellers[key])
                        .join(','),
                    locationIds: Object.keys(filters.locations)
                        .filter((key) => filters.locations[key])
                        .join(','),
                    rankBy,
                    searchText,
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    material: material === 'All' ? undefined : material,
                }
            );
            dispatch(setLastUpdated());
            setEmployeesData(ranks);
        } catch (e) {
            setAlert('Could not get employee ranking', 'error');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        getEmployeesData();
    }, [pageNum, filters, searchText, rankBy, material, dateRange]);

    const numPages = useMemo(
        () => Math.ceil(employeesData.totalRecords / PAGE_SIZE) || 1,
        [employeesData.totalRecords]
    );

    return (
        <ReportLayout
            noResultsText={
                <Typography>
                    None of your companies have the &quot;Sales Rep Tracking&quot; feature enabled.
                    <br />
                    Please contact support at <b>partner@moutfitters.com</b> if you wish to opt into the feature.
                </Typography>
            }
            title='Employee Ranks'
            pageNum={pageNum}
            setPageNum={setPageNum}
            numPages={numPages}
            loadingData={loadingData || isLoading}
            tableOptions={{
                headers: [
                    'Rank',
                    'Name',
                    'Email',
                    'Company',
                    'Reseller',
                    'Location',
                    '# Cuts',
                    'Warranty %',
                    '# Successful Cuts',
                    '# Warranty Cuts',
                ],
                data: employeesData.employees.map((employee, idx) => [
                    employee.EmployeeRank,
                    <Tooltip
                        key={`${employee.Email} - ${employee.LocationID} - tooltip`}
                        title={'Click to view more details'}>
                        <Link
                            sx={{ cursor: 'pointer' }}
                            href={`${AppRoutes.EmployeeDetails}?startDate=${encodeURIComponent(
                                dateRange.startDate
                            )}&endDate=${encodeURIComponent(dateRange.endDate)}&email=${encodeURIComponent(
                                employee.Email
                            )}&locationId=${encodeURIComponent(employee.LocationID)}`}>
                            {employee.Name}
                        </Link>
                    </Tooltip>,
                    employee.Email,
                    nameMap?.companies[employee.CompanyID] ? nameMap?.companies[employee.CompanyID] : 'Unknown',
                    nameMap?.resellers[employee.ResellerID] ? nameMap?.resellers[employee.ResellerID] : 'None',
                    nameMap?.locations[employee.LocationID] ? nameMap?.locations[employee.LocationID] : 'Unknown',
                    employee.TotalCuts,
                    employee.WarrantyClaimPercentage,
                    <TotalCuts
                        key={`total-cuts-success: ${employee.WarrantyClaimCuts} ${employee.SuccessfulCuts} ${idx}`}
                        totalCuts={employee.TotalCuts}
                        numCutType={employee.SuccessfulCuts}
                        color={'success'}
                        minWidth='150px'
                    />,
                    <TotalCuts
                        key={`total-cuts-warranty: ${employee.WarrantyClaimCuts} ${employee.SuccessfulCuts} ${idx}`}
                        totalCuts={employee.TotalCuts}
                        numCutType={employee.WarrantyClaimCuts}
                        color={'error'}
                        minWidth='150px'
                    />,
                ]),
                sort: [basicSort, basicSort, basicSort, basicSort, basicSort, basicSort],
                summaries: [none, none, none, none, none, none],
            }}
            action={
                <EmployeeExport
                    filters={filters}
                    searchText={searchText}
                    rankBy={rankBy}
                    material={material}
                    dateRange={dateRange}
                />
            }
            filter={
                <Grid
                    display={'flex'}
                    flexDirection={'row'}
                    flexWrap={'wrap'}
                    justifyContent={'flex-start'}
                    alignItems={'flex-start'}>
                    <TextField
                        placeholder={'Type employee email or name'}
                        type={'search'}
                        InputProps={{ endAdornment: <Search sx={{ fontSize: '20px' }} /> }}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{
                            '.MuiInputBase-root': {
                                height: '30px !important',
                            },
                            backgroundColor: 'whitesmoke',
                            opacity: '60%',
                            width: '300px',
                            borderWidth: '0px',
                            borderRadius: '5px',
                            height: '35px !important',
                            marginTop: '3px',
                            marginLeft: '10px',
                        }}
                    />
                    <Box display={'flex'} flexDirection={'row'} gap={1} flex={1} width={'100%'} ml={1}>
                        <OrgFilters
                            handleClear={() => {
                                /* empty function */
                            }}
                            orgFilters={filters}
                            setOrgFilters={setFilter as any}
                        />
                    </Box>
                    <Select
                        value={rankBy}
                        onChange={(e) =>
                            setRankBy(e.target.value as 'totalCuts' | 'warranty' | 'successful' | 'warrantyPercentage')
                        }
                        IconComponent={(props) => <KeyboardArrowDown {...props} sx={{ fontSize: '20px' }} />}
                        renderValue={(value) => `Rank By: ${LABEL_MAP[value]}`}
                        sx={{
                            height: '30px',
                            marginLeft: '10px',
                            marginTop: '3px',
                            width: '175px',
                            fontSize: '12px',
                            color: 'grey',
                            fontWeight: 500,
                        }}>
                        {Object.keys(LABEL_MAP).map((key) => (
                            <MenuItem key={key} value={key}>
                                {LABEL_MAP[key]}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        value={material}
                        onChange={(e) => setMaterial(e.target.value as keyof typeof MaterialIdMap)}
                        IconComponent={(props) => <KeyboardArrowDown {...props} sx={{ fontSize: '20px' }} />}
                        renderValue={(value) =>
                            `Material: ${value === 'All' ? 'All' : (MaterialIdMap as any)[value][0]}`
                        }
                        sx={{
                            height: '30px',
                            marginLeft: '10px',
                            marginTop: '3px',
                            width: '175px',
                            fontSize: '12px',
                            color: 'grey',
                            fontWeight: 500,
                        }}>
                        <MenuItem value={'All'}>All</MenuItem>

                        {Object.keys(MaterialIdMap).map((key) => (
                            <MenuItem key={key} value={key}>
                                {(MaterialIdMap as any)[key][0]}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            }
        />
    );
};
